import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { DeleteModalComponent } from 'app/shared/delete-modal/delete-modal.component';
import { EntityService } from 'app/shared/entity.service';
import { ToastrService } from 'ngx-toastr';
import {
  _GlobalControlItem,
  GLOBAL_CONTROLS_ERROR_TOOLTIP,
  GLOBAL_CONTROLS_TOOLTIP,
  GLOBAL_CONTROLS_SUCCESS_MESSAGE,
  GlobalControlsConnectionsExtended,
  GLOBAL_CONTROLS_ERROR_MESSAGE,
  GLOBAL_CONTROL_REMOVE_SUCCESS_MESSAGE,
} from './global-controls.constant';
import { GlobalControlService } from './global-control.service';
import { DeleteModalTypeEnum } from 'app/shared/enums/deleteModal.enum';

@Component({
  selector: 'cygov-global-controls',
  templateUrl: './global-controls.component.html',
  styleUrls: ['./global-controls.component.scss'],
  standalone: false,
})
export class GlobalControlsComponent implements OnInit, OnDestroy {
  @Input() currentRootEntity: string = null; //* Current Root Entity

  //* Constants
  readonly INFO_TOOLTIP: string = GLOBAL_CONTROLS_TOOLTIP;
  readonly ERROR_TOOLTIP: string = GLOBAL_CONTROLS_ERROR_TOOLTIP;
  readonly SUCCESS_MESSAGE: string = GLOBAL_CONTROLS_SUCCESS_MESSAGE;
  readonly REMOVE_SUCCESS_MESSAGE: string = GLOBAL_CONTROL_REMOVE_SUCCESS_MESSAGE;
  readonly ERROR_MESSAGE: string = GLOBAL_CONTROLS_ERROR_MESSAGE;

  isEditMode: boolean = false; //* Edit mode toggle
  isLogsVisible: boolean = false; //* Toggle for logs visibility
  isAddActionAllowed: boolean = false; //* Flag to allow add action
  isOriginDropDownOpen: boolean = false; //* Flag to check if origin dropdown is open
  isDestinationDropDownOpen: boolean = false; //* Flag to check if destination dropdown is open
  isLoading: boolean = false; //* Flag to indicate loading state
  isAddingConnection: boolean = false; //* Flag to prevent multiple rapid clicks
  dropdownResetKey: number = 0; //* Key to force dropdown reset via custom dropdown component

  allSubEntityList: _GlobalControlItem[] = []; //* List of all sub-entities with frameworks
  originSubEntityList: _GlobalControlItem[] = []; //* List of origin sub-entities
  destinationSubEntityList: _GlobalControlItem[] = []; //* List of destination sub-entities

  selectedOrigin: _GlobalControlItem = null; //* currently selected origin sub-entity
  selectedDestinationList: _GlobalControlItem[] = []; //* currently selected destination sub-entities

  //* Already created global control Connections
  allGlobalControlItems: GlobalControlsConnectionsExtended[] = [];

  //* Modal options for delete confirmation
  ngbModalDeleteConnectionOptions: NgbModalOptions = {
    size: 'sm',
    windowClass: 'delete-entity-modal delete-collection-framework-modal delete-connection-modal',
    backdropClass:
      'delete-entity-backdrop-modal delete-collection-framework-backdrop-modal delete-connection-modal-backdrop',
    backdrop: 'static',
  };
  closeModalInstance = null;

  constructor(
    private modalService: NgbModal,
    private entityService: EntityService,
    private toastrService: ToastrService,
    private globalCOntrolService: GlobalControlService
  ) {}

  ngOnInit() {
    this.initializeSubEntityList();
    this.getGlobalControlItems();
  }

  //* Refresh origin list based on current global control items
  private refreshOriginList(): void {
    this.originSubEntityList = this.filterOriginList();

    // If current origin is no longer valid, clear it
    if (
      this.selectedOrigin &&
      !this.originSubEntityList.find(entity => entity.subEntityId === this.selectedOrigin.subEntityId)
    ) {
      this.selectedOrigin = null;
      this.selectedDestinationList = [];
      this.destinationSubEntityList = [];
      this.dropdownResetKey++;
    }
  }

  //* Public method to refresh origin list when component becomes visible
  public refreshOriginListOnDemand(): void {
    this.refreshOriginList();
  }

  //* Initialize sub-entity list based on the current root entity
  private async initializeSubEntityList() {
    try {
      if (!this.currentRootEntity) {
        this.toastrService.error('Root entity is not set');
        return;
      }
      const entitiesData = await this.entityService.getEntitiesWithFrameworksByParentId(this.currentRootEntity);
      this.allSubEntityList = this.globalCOntrolService.formatSubEntityList(entitiesData);
      this.originSubEntityList = this.filterOriginList();
    } catch (error) {
      console.error('Error initializing sub-entity list:', error);
    }
  }

  //* Fetch global control items from the service
  private async getGlobalControlItems() {
    try {
      this.isLoading = true;
      const data = (await this.globalCOntrolService.getGlobalConnectionsByRootEntity(this.currentRootEntity)) as any;
      this.allGlobalControlItems = data || [];
      if (this.allGlobalControlItems.length > 0) {
        this.allGlobalControlItems = this.globalCOntrolService.mapOriginAndDestination(this.allGlobalControlItems);
      } else {
        this.allGlobalControlItems = [];
      }
      // Refresh origin list after fetching global control items
      this.refreshOriginList();
      this.updateAddButtonState();
    } catch (error) {
      console.error('Error fetching global control items:', error);
      this.toastrService.error('Failed to fetch global control items. Please try again later.');
    } finally {
      this.isLoading = false;
    }
  }

  //* when origin is selected, filter destination sub-entities
  selectOrigin($event: any) {
    this.selectedOrigin = $event?.length ? $event[0] : null;
    this.destinationSubEntityList = this.filterDestinationList(this.selectedOrigin);

    // Clear destination selection when origin changes
    if (!this.selectedOrigin) {
      this.selectedDestinationList = [];
    }

    this.updateAddButtonState();
  }

  //* filter Origin list based on current active connections
  filterOriginList(): _GlobalControlItem[] {
    // TODO - Implement filtering logic for origin sub-entities
    if (!this.allGlobalControlItems || this.allGlobalControlItems.length === 0) {
      return this.allSubEntityList;
    }

    // Get all entities that are already destinations in existing connections
    const usedDestinationIds = this.allGlobalControlItems.map(connection => connection.destinationSubEntityId);

    // Filter out entities that are already destinations
    return this.allSubEntityList.filter(entity => !usedDestinationIds.includes(entity.subEntityId));
  }

  //* Filter destination sub-entities based on selected origin
  filterDestinationList(origin: _GlobalControlItem): _GlobalControlItem[] {
    if (!origin) {
      return [];
    }

    // Get entities to exclude from destinations
    const excludedEntityIds = new Set<string>();

    // 1. Exclude the origin entity itself
    excludedEntityIds.add(origin.subEntityId);

    // 2. Exclude entities that are already origins in existing connections
    if (this.allGlobalControlItems && this.allGlobalControlItems.length > 0) {
      const usedOriginIds = this.allGlobalControlItems.map(connection => connection.originSubEntityId);
      usedOriginIds.forEach(id => excludedEntityIds.add(id));
    }

    // 3. Exclude entities that are already destinations in existing connections
    if (this.allGlobalControlItems && this.allGlobalControlItems.length > 0) {
      const usedDestinationIds = this.allGlobalControlItems.map(connection => connection.destinationSubEntityId);
      usedDestinationIds.forEach(id => excludedEntityIds.add(id));
    }

    // Filter based on framework compatibility and excluded entities
    return this.allSubEntityList.filter(
      subEntity =>
        !excludedEntityIds.has(subEntity.subEntityId) && subEntity.id.split('#')[1] === origin.id.split('#')[1]
    );
  }

  //* when destination is selected, update the list of selected destinations
  selectDestination($event: any) {
    this.selectedDestinationList = $event || [];
    this.updateAddButtonState();
  }

  //* Add global control connections
  async addButtonClicked() {
    // Prevent multiple rapid clicks
    if (this.isAddingConnection) {
      return;
    }

    if (!this.selectedOrigin || this.selectedDestinationList.length === 0) {
      this.toastrService.warning('Please select both origin and destination entities before adding.');
      return;
    }

    // Set flag to prevent multiple clicks
    this.isAddingConnection = true;
    this.updateAddButtonState();

    try {
      const response = await this.globalCOntrolService.addGlobalControlConnections(
        this.currentRootEntity,
        this.selectedOrigin,
        this.selectedDestinationList
      );
      if (response?.length === this.selectedDestinationList?.length) {
        this.toastrService.success(this.SUCCESS_MESSAGE);
        if (response.length > 0) {
          this.allGlobalControlItems = this.allGlobalControlItems.concat(response);
          this.allGlobalControlItems = this.globalCOntrolService.mapOriginAndDestination(this.allGlobalControlItems);
        }
        // Clear selections and reset dropdowns
        this.selectedOrigin = null;
        this.selectedDestinationList = [];
        this.destinationSubEntityList = [];
        this.dropdownResetKey++; // Force dropdown reset

        // Refresh origin list to exclude newly used destinations
        this.originSubEntityList = this.filterOriginList();
      } else {
        this.toastrService.error(this.ERROR_MESSAGE);
        throw new Error(`Failed to create global connections. ${JSON.stringify(response)}`);
      }
    } catch (error) {
      console.error('Error adding global controls:', error);
    } finally {
      // Reset flag and update button state
      this.isAddingConnection = false;
      this.updateAddButtonState();
    }
  }

  //* Check if connections already exist for the selected origin and destinations
  private checkForExistingConnections(): string[] {
    if (!this.selectedOrigin || !this.selectedDestinationList || this.selectedDestinationList.length === 0) {
      return [];
    }

    const existingConnections: string[] = [];

    for (const destination of this.selectedDestinationList) {
      const connectionExists = this.allGlobalControlItems.some(
        connection =>
          connection.originSubEntityId === this.selectedOrigin.subEntityId &&
          connection.destinationSubEntityId === destination.subEntityId &&
          connection.originAssessmentStandardFrameworkId === this.selectedOrigin.id &&
          connection.destinationAssessmentStandardFrameworkId === destination.id
      );

      if (connectionExists) {
        existingConnections.push(destination.displayName);
      }
    }

    return existingConnections;
  }

  //* Update the add button state based on current selections
  private updateAddButtonState(): void {
    const hasValidSelection =
      this.selectedOrigin && this.selectedDestinationList && this.selectedDestinationList.length > 0;

    if (!hasValidSelection || this.isAddingConnection) {
      this.isAddActionAllowed = false;
      return;
    }

    // Check if any of the selected connections already exist
    const existingConnections = this.checkForExistingConnections();
    this.isAddActionAllowed = existingConnections.length === 0;
  }

  //* Toggle edit mode for global controls
  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;
  }

  //* Toggle visibility of logs
  toggleLogsVisibility(): void {
    this.isLogsVisible = !this.isLogsVisible;
  }

  //* Show confirmation modal for deletion of global control connection
  showConfirmationDeletion(idx: number): void {
    const currentGlobalItem = this.allGlobalControlItems[idx];
    const modalRef = this.modalService.open(DeleteModalComponent, this.ngbModalDeleteConnectionOptions);
    modalRef.componentInstance.titleText = 'REMOVE GLOBAL CONTROL CONNECTION';
    modalRef.componentInstance.modalType = DeleteModalTypeEnum.DELETE_GLOBAL_CONTROL;
    modalRef.componentInstance.isSvgVisible = false;
    modalRef.componentInstance.isFromGlobalControls = true;
    modalRef.componentInstance.deleteEntityModal = true;
    modalRef.componentInstance.isConfirm2Required = false;
    modalRef.componentInstance.globalControlOrigin = currentGlobalItem?.originName || '';
    modalRef.componentInstance.globalControlDestination = currentGlobalItem?.destinationName || '';
    modalRef.componentInstance.deleteFramework.subscribe(async (deleteConfirm: boolean) => {
      if (deleteConfirm) {
        await this.deleteGlobalControl(idx, currentGlobalItem);
      }
    });

    this.closeModalInstance = modalRef;
  }

  async deleteGlobalControl(idx: number, connection: GlobalControlsConnectionsExtended): Promise<void> {
    await this.globalCOntrolService.deleteGlobalControlConnection(connection, this.currentRootEntity);
    this.toastrService.success(this.REMOVE_SUCCESS_MESSAGE);
    this.allGlobalControlItems.splice(idx, 1);

    // Refresh origin list to include newly available entities
    this.refreshOriginList();

    this.updateAddButtonState();
  }

  handleOriginDropdownToggleEvent($event: boolean): void {
    this.isOriginDropDownOpen = $event;
    if (this.isOriginDropDownOpen) {
      this.closeDestinationDropdown();
    }
  }

  handleDestinationDropdownToggleEvent($event: boolean): void {
    this.isDestinationDropDownOpen = $event;
    if (this.isDestinationDropDownOpen) {
      this.closeOriginDropdown();
      this.restoreDestinationSelectionState();
    }
  }

  //* Restore selected state when dropdown opens
  private restoreDestinationSelectionState(): void {
    if (this.selectedDestinationList && this.selectedDestinationList.length > 0) {
      this.destinationSubEntityList = this.destinationSubEntityList.map(item => ({
        ...item,
        selected: this.selectedDestinationList.some(selected => selected.id === item.id),
      }));
    }
  }

  closeAllDropdowns(): void {
    this.closeOriginDropdown();
    this.closeDestinationDropdown();
  }

  closeOriginDropdown(): void {
    this.isOriginDropDownOpen = false;
  }

  closeDestinationDropdown(): void {
    this.isDestinationDropDownOpen = false;
  }

  //* Get selected destination names for preSelectedItems
  getSelectedDestinationNames(): string[] {
    if (!this.selectedDestinationList || this.selectedDestinationList.length === 0) {
      return [];
    }
    return this.selectedDestinationList.map(item => item.displayName);
  }

  getSelectedOriginName(): string[] {
    if (!this.selectedOrigin) {
      return [];
    }
    return [this.selectedOrigin.displayName];
  }

  ngOnDestroy(): void {
    if (this.closeModalInstance) {
      this.closeModalInstance.close();
    }
  }
}
